import {
  Parser,
  ScriptError,
  UNITS,
  blockAround,
  buildPreset,
  expr as exprSource,
  identifyPreset,
  parseTemplate,
  withBlockAround,
  workoutExercises,
  type AutoLine,
  type DynamicLine,
  type ExerciseRef,
  type Expr,
  type Line,
  type Mods,
  type Preset,
  type SetLine as SetLineNode,
  type TemplateNode,
} from '@gymcloud/mixedscript'
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useDataState } from '~/data/data-context'
import { BoardTemplate } from '~/lib/board-template'
import { staticExprProblem } from '~/lib/expr-guard'
import type { CodeProblem } from '~/lib/code-problems'
import {
  MODES,
  around,
  blankFor,
  blockBody,
  blockFrom,
  call,
  describeLine,
  describeMode,
  dimOf,
  labelOf,
  lineSource,
  modeOf,
  primaryLabel,
  primaryText,
  primaryTips,
  primaryUnit,
  readPrimary,
  sameMode,
  toAmrap,
  toExact,
  toRange,
  weightText,
  withAround,
  type Mode,
} from '~/lib/line-shape'
import { useSelectOnFocus } from '~/lib/select-on-focus'

import { CodeEditor } from './code-editor'

import {
  ChoiceChip,
  FieldSep,
  NumberField,
  PanelBlock,
  PanelRow,
  RowPopover,
  TogglePill,
} from './editor-fields'
import { FloatOut } from './float-out'
import { Menu, MenuItem } from './editor-menu'
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  CopyIcon,
  DeleteIcon,
  FlameIcon,
  ListIcon,
  NoteIcon,
  VideoIcon,
} from './icons'

/**
 * One line of an exercise — `@specs/features/workout.spec.md`, The template editor. The row
 * reads as the numbers it stands for, each wearing its name in small print and editable in
 * place — a range or a formula shows as the script writes it. The chevron floats the line's
 * full editor over the rows beneath, so opening it never reflows the card.
 */

/** Where a set line sits, for dragging and for the moves its menu offers. */
export interface LineAt extends ExerciseRef {
  readonly line: number
}

export function LineRow({
  line,
  at,
  lineCount,
  restAllowed,
  onLine,
  onMove,
  onRemove,
  onDuplicate,
  onDragStart,
  onDropOn,
  autoFocus,
}: {
  line: Line
  at: LineAt
  lineCount: number
  restAllowed: boolean
  onLine: (line: Line) => void
  onMove: (to: number) => void
  onRemove: () => void
  onDuplicate: () => void
  onDragStart: () => void
  onDropOn: () => void
  /** A freshly added line starts in its first field, ready to be typed. */
  autoFocus?: boolean | undefined
}) {
  const [open, setOpen] = useState(false)
  const summary = describeLine(line)
  const identified = useMemo(() => (line.type === 'dynamic' ? identifyPreset(line) : null), [line])
  // What this line asks for around its sets, or null when it says all of that in code.
  const held = around(line, identified)

  return (
    <div
      role="group"
      aria-label={summary}
      draggable
      // An open editor means this row is the one being worked on, so it stays lit. The wash
      // under the pointer belongs to what is being pointed at, and every part of the row is
      // its own target, so the row itself takes none.
      className={`relative rounded-xl px-1 transition-colors ${open ? 'bg-white/5' : ''}`}
      onDragStart={(event) => {
        event.stopPropagation()
        onDragStart()
      }}
      onDragOver={(event) => {
        event.preventDefault()
      }}
      onDrop={(event) => {
        event.stopPropagation()
        onDropOn()
      }}
    >
      {/*
        Every row is the same row: the warm-up mark, then what the line says with the chevron
        at the end of it, then the rest, the filming and the menu. Only the middle changes with
        the kind of line, so the marks sit in one column down the card rather than wherever the
        numbers left off. A code line has none of them — it says all of that in its own yield
        lines — so what it says takes the whole width instead, and the menu stays where it is.
      */}
      <div className="flex items-center gap-1 py-1">
        {held === null ? null : (
          <WarmupMark
            on={held.warmup}
            onToggle={() => {
              onLine(withAround(line, { ...held, warmup: !held.warmup }))
            }}
          />
        )}

        {/*
          What the line says, and the chevron that opens it, as one target: pressing anywhere
          across them opens the line, including the room between the numbers. A press that
          lands on a number is a press on that number — the field is a target of its own, and
          typing into one was never a way of asking to open anything.
        */}
        {/*
          The keyboard's way in is the chevron inside it, which is a button and says whether
          the line is open; this space is the pointer's own affordance on top of that, so it
          takes no role and no tab stop of its own.
        */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className="flex min-w-0 flex-1 items-center rounded-xl transition-colors hover:bg-hover"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('input, textarea, select, button, label')) {
              return
            }
            setOpen(!open)
          }}
        >
          {line.type === 'set' ? (
            <SetRow line={line} onLine={onLine} autoFocus={autoFocus} />
          ) : (
            <button
              type="button"
              aria-expanded={open}
              className="min-w-0 flex-1 rounded-lg px-2 py-1 text-left text-sm text-ink-muted transition-colors hover:text-ink"
              onClick={() => {
                setOpen(!open)
              }}
            >
              <span className="block truncate">
                {line.type === 'auto' ? '⟳ ' : '⟲ '}
                {summary}
              </span>
              <span
                aria-hidden
                className="block text-[0.6875rem] uppercase tracking-wider text-ink-muted/80"
              >
                {line.type === 'auto'
                  ? line.warmup
                    ? 'Warm-up ramp'
                    : 'Ramp'
                  : identified
                    ? identified.preset.label
                    : 'Code'}
              </span>
            </button>
          )}

          <button
            type="button"
            aria-label={open ? 'Close this line' : 'Open this line'}
            aria-expanded={open}
            className={`grid size-8 shrink-0 place-items-center rounded-full transition-colors hover:bg-hover hover:text-ink ${
              open ? 'bg-white/10 text-ink' : 'text-ink-muted'
            }`}
            onClick={() => {
              setOpen(!open)
            }}
          >
            <ChevronDownIcon
              className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {held !== null && restAllowed ? (
          <span className="flex w-16 shrink-0 justify-center">
            <NumberField
              micro
              clearable
              label="Rest"
              tips={REST_TIPS}
              value={held.mods.rest ? exprSource(held.mods.rest) : ''}
              unit={UNITS.seconds}
              onValue={(rest) => {
                onLine(
                  withAround(line, { ...held, mods: { ...held.mods, rest: rest ?? undefined } }),
                )
              }}
            />
          </span>
        ) : null}

        {held === null ? null : (
          <FilmMark
            on={held.mods.video === true}
            onToggle={() => {
              onLine(
                withAround(line, {
                  ...held,
                  mods: { ...held.mods, video: held.mods.video === true ? undefined : true },
                }),
              )
            }}
          />
        )}

        <Menu label={`Options for ${summary}`}>
          <MenuItem
            icon={<ArrowUpIcon className="size-4" />}
            disabled={at.line === 0}
            onSelect={() => {
              onMove(at.line - 1)
            }}
          >
            Move up
          </MenuItem>
          <MenuItem
            icon={<ArrowDownIcon className="size-4" />}
            disabled={at.line === lineCount - 1}
            onSelect={() => {
              onMove(at.line + 1)
            }}
          >
            Move down
          </MenuItem>
          <MenuItem icon={<CopyIcon className="size-4" />} onSelect={onDuplicate}>
            Duplicate line
          </MenuItem>
          <MenuItem icon={<DeleteIcon className="size-4" />} danger onSelect={onRemove}>
            Remove this line
          </MenuItem>
        </Menu>
      </div>

      {held?.mods.notes !== undefined && !open ? (
        <p className="flex items-center gap-1 px-2 pb-1 text-xs text-ink-muted">
          <NoteIcon aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">{held.mods.notes}</span>
        </p>
      ) : null}

      <RowPopover
        open={open}
        label={`Line settings for ${summary}`}
        onClose={() => {
          setOpen(false)
        }}
      >
        <LinePanel line={line} restAllowed={restAllowed} identified={identified} onLine={onLine} />
      </RowPopover>
    </div>
  )
}

/** A static line's numbers in a row, each wearing its name in small print. */
function SetRow({
  line,
  onLine,
  autoFocus,
}: {
  line: SetLineNode
  onLine: (line: Line) => void
  autoFocus?: boolean | undefined
}) {
  const primary = readPrimary(line.primary)
  const unit = primaryUnit(primary.dim)

  // The numbers and nothing else — what surrounds them is the row's, and sits in its own
  // column. They read as one line and never fold: a card wide enough for what it prescribes
  // is worth more than a card that keeps its width and hides the shape of the set.
  return (
    <span className="flex min-w-max flex-1 items-stretch justify-center gap-0.5 text-sm">
      <NumberField
        micro
        autoFocus={autoFocus}
        label="Sets"
        tips="How many sets of this — three is a good place to start."
        value={line.count ? exprSource(line.count) : '1'}
        unit={UNITS.reps}
        onValue={(count) => {
          onLine({ ...line, count })
        }}
      />
      <FieldSep>×</FieldSep>
      <NumberField
        micro
        sugared
        label={primaryLabel(primary.dim)}
        tips={primaryTips(primary.dim)}
        value={primaryText(line.primary)}
        unit={unit}
        onValue={(value) => {
          if (value !== null) {
            onLine({ ...line, primary: value })
          }
        }}
      />
      <FieldSep>@</FieldSep>
      <NumberField
        micro
        clearable
        sugared
        label="Weight"
        tips={WEIGHT_TIPS}
        value={weightText(line.weight)}
        unit={UNITS.kg}
        onValue={(weight) => {
          onLine({ ...line, weight })
        }}
      />
    </span>
  )
}

/**
 * The two things a row says about itself beyond its numbers. Both are marked the same way:
 * always there, lit when they are on, one press either way.
 */
function WarmupMark({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Mark
      on={on}
      title={
        on
          ? 'These are warm-up sets — press to make them working sets'
          : 'Press to make these warm-up sets'
      }
      label={on ? 'Turn warm-up off' : 'Turn warm-up on'}
      onToggle={onToggle}
    >
      <FlameIcon aria-hidden className={`size-4 ${on ? 'text-warmup' : 'text-ink-muted/50'}`} />
    </Mark>
  )
}

function FilmMark({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Mark
      on={on}
      title={
        on
          ? 'These sets ask to be filmed — press to stop asking'
          : 'Press to ask for these sets to be filmed'
      }
      label={on ? 'Turn filming off' : 'Turn filming on'}
      onToggle={onToggle}
    >
      <VideoIcon aria-hidden className={`size-4 ${on ? 'text-accent' : 'text-ink-muted/50'}`} />
    </Mark>
  )
}

function Mark({
  on,
  title,
  label,
  onToggle,
  children,
}: {
  on: boolean
  title: string
  label: string
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      aria-pressed={on}
      // The hover box runs the height of the row, the way the fields beside it do.
      className="flex w-9 shrink-0 self-stretch items-center justify-center rounded-lg transition-colors hover:bg-hover"
      onClick={onToggle}
    >
      {children}
    </button>
  )
}

// --- the opened line ---

function LinePanel({
  line,
  restAllowed,
  identified,
  onLine,
}: {
  line: Line
  restAllowed: boolean
  identified: { preset: Preset; params: Record<string, string> } | null
  onLine: (line: Line) => void
}) {
  const mode = modeOf(line, identified)
  // Fields keep no state across a change of what the line is — a weight typed into a reps
  // line has no business reappearing on the time line that replaced it.
  const modeKey =
    line.type === 'set'
      ? `set-${dimOf(line)}`
      : line.type === 'auto'
        ? 'auto'
        : identified
          ? `preset-${identified.preset.id}`
          : 'code'

  return (
    <div className="flex min-h-0 flex-col">
      {/* What the line is stays put while the fields scroll — the way out is never hidden. */}
      <div className="shrink-0 border-b border-white/5">
        <TypePicker
          mode={mode}
          onPick={(option) => {
            if (!sameMode(option, mode)) {
              onLine(blankFor(option, line))
            }
          }}
        />
      </div>

      <div key={modeKey} className="scroll-y min-h-0 space-y-3 overscroll-contain p-3">
        {line.type === 'set' ? (
          <StaticEditor line={line} onLine={onLine} />
        ) : line.type === 'auto' ? (
          <RampFields line={line} onLine={onLine} />
        ) : identified !== null ? (
          <PresetFields line={line} identified={identified} onLine={onLine} />
        ) : (
          <CodeField line={line} onLine={onLine} />
        )}
        {/* Whatever shape the line is, what surrounds it is asked for the same way — where
            there is anything to ask. A code line writes all of it on its own yield lines. */}
        {around(line, identified) === null ? null : (
          <div className="space-y-3 border-t border-white/5 pt-3">
            <LineExtras
              line={line}
              restAllowed={restAllowed}
              identified={identified}
              onLine={onLine}
            />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * What a line is, picked from a select that says what each choice means — the label alone
 * does not tell a first-timer what a Linear progression will do to their numbers.
 */
function TypePicker({ mode, onPick }: { mode: Mode; onPick: (mode: Mode) => void }) {
  const [open, setOpen] = useState(false)
  const held = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      if (held.current && !held.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  return (
    <div ref={held} className="relative">
      <button
        type="button"
        aria-label="This line is"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-surface-bright ${
          open ? 'bg-surface-bright' : 'bg-surface-raised'
        }`}
        onClick={() => {
          setOpen(!open)
        }}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-ink">{labelOf(mode)}</span>
          <span className="block text-xs text-ink-muted">{describeMode(mode)}</span>
        </span>
        <ChevronDownIcon
          aria-hidden
          className={`size-5 shrink-0 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <FloatOut open={open}>
        <div
          role="listbox"
          aria-label="What this line is"
          className="absolute inset-x-0 top-full z-30 mt-1 max-h-72 overflow-y-auto overscroll-contain rounded-xl bg-surface-bright p-1 shadow-2xl ring-1 ring-white/10"
        >
          {MODES.map((option) => (
            <button
              key={labelOf(option)}
              type="button"
              role="option"
              aria-selected={sameMode(option, mode)}
              className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-hover"
              onClick={() => {
                onPick(option)
                setOpen(false)
              }}
            >
              <span
                className={`block text-sm ${sameMode(option, mode) ? 'text-accent' : 'text-ink'}`}
              >
                {labelOf(option)}
              </span>
              <span className="block text-xs text-ink-muted">{describeMode(option)}</span>
            </button>
          ))}
        </div>
      </FloatOut>
    </div>
  )
}

function StaticEditor({ line, onLine }: { line: SetLineNode; onLine: (line: Line) => void }) {
  const primary = readPrimary(line.primary)
  const unit = primaryUnit(primary.dim)
  const noun = primaryLabel(primary.dim).toLowerCase()

  return (
    <div className="space-y-3">
      <PanelRow label="Sets">
        <NumberField
          boxed
          label="Sets"
          tips="How many sets of this — three is a good place to start."
          value={line.count ? exprSource(line.count) : '1'}
          unit={UNITS.reps}
          onValue={(count) => {
            onLine({ ...line, count })
          }}
        />
      </PanelRow>

      <PanelBlock label={primaryLabel(primary.dim)}>
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={`How the ${noun} are asked`}
        >
          <ChoiceChip
            label="Fixed"
            title={`The same ${noun} every set.`}
            active={primary.kind === 'exact'}
            onPick={() => {
              onLine({ ...line, primary: toExact(line.primary, primary, unit) })
            }}
          />
          <ChoiceChip
            label="Range"
            title="Anywhere between the two counts as done."
            active={primary.kind === 'range'}
            onPick={() => {
              onLine({ ...line, primary: toRange(line.primary, primary, unit) })
            }}
          />
          {primary.dim === 'none' ? (
            <ChoiceChip
              label="As many as possible"
              title="At least this many, and however many more you have in you."
              active={primary.kind === 'amrap'}
              onPick={() => {
                onLine({ ...line, primary: toAmrap(line.primary, primary) })
              }}
            />
          ) : null}
        </div>

        {primary.kind === 'range' ? (
          <div className="flex items-center gap-1.5">
            <NumberField
              boxed
              label={`From ${noun}`}
              value={exprSource(primary.low)}
              unit={unit}
              onValue={(low) => {
                if (low !== null) {
                  onLine({ ...line, primary: call('range', [low, primary.high]) })
                }
              }}
            />
            <FieldSep>to</FieldSep>
            <NumberField
              boxed
              label={`To ${noun}`}
              value={exprSource(primary.high)}
              unit={unit}
              onValue={(high) => {
                if (high !== null) {
                  onLine({ ...line, primary: call('range', [primary.low, high]) })
                }
              }}
            />
          </div>
        ) : primary.kind === 'amrap' ? (
          <div className="flex items-center gap-1.5">
            <FieldSep>at least</FieldSep>
            <NumberField
              boxed
              label="At least reps"
              value={exprSource(primary.low)}
              unit={UNITS.reps}
              onValue={(low) => {
                if (low !== null) {
                  onLine({ ...line, primary: call('amrap', [low]) })
                }
              }}
            />
          </div>
        ) : (
          <NumberField
            boxed
            sugared
            label={primaryLabel(primary.dim)}
            tips={primaryTips(primary.dim)}
            value={primaryText(line.primary)}
            unit={unit}
            onValue={(value) => {
              if (value !== null) {
                onLine({ ...line, primary: value })
              }
            }}
          />
        )}
      </PanelBlock>

      <PanelRow label="Weight">
        <NumberField
          boxed
          clearable
          sugared
          label="Weight"
          tips={WEIGHT_TIPS}
          value={weightText(line.weight)}
          unit={UNITS.kg}
          onValue={(weight) => {
            onLine({ ...line, weight })
          }}
        />
      </PanelRow>
    </div>
  )
}

/**
 * What surrounds a line whatever shape it is: the rest before it, whether it asks to be
 * filmed, anything written on it. A static line writes these on itself; a progression or a
 * code block carries them as its own properties. A ramp has nowhere in the language to put
 * them, so it is offered only the one it has.
 */
function LineExtras({
  line,
  restAllowed,
  identified,
  onLine,
}: {
  line: Line
  restAllowed: boolean
  identified: { preset: Preset } | null
  onLine: (line: Line) => void
}) {
  const held = around(line, identified)
  if (held === null) {
    return (
      <p className="text-xs text-ink-muted">
        A code line says all of this on the sets it yields — `yield 3 x 5 @ 60kg rest 3m video` — so
        there is nothing to fill in here.
      </p>
    )
  }
  const change = (next: Partial<{ warmup: boolean; mods: Mods }>) => {
    onLine(withAround(line, { ...held, ...next }))
  }

  return (
    <>
      {restAllowed ? (
        <PanelRow label="Rest before">
          <NumberField
            boxed
            clearable
            label="Rest before"
            tips={REST_TIPS}
            value={held.mods.rest ? exprSource(held.mods.rest) : ''}
            unit={UNITS.seconds}
            onValue={(rest) => {
              change({ mods: { ...held.mods, rest: rest ?? undefined } })
            }}
          />
        </PanelRow>
      ) : (
        <p className="text-xs text-ink-muted">
          Rest lives on the superset, taken between its rounds.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        <TogglePill
          label="Warm-up"
          icon={<FlameIcon aria-hidden className="size-3.5" />}
          active={held.warmup}
          onToggle={() => {
            change({ warmup: !held.warmup })
          }}
        />
        <TogglePill
          label="Film it"
          icon={<VideoIcon aria-hidden className="size-3.5" />}
          active={held.mods.video === true}
          onToggle={() => {
            change({ mods: { ...held.mods, video: held.mods.video === true ? undefined : true } })
          }}
        />
      </div>

      <PanelRow label="Notes">
        <input
          aria-label="Notes"
          className="w-full rounded-lg bg-surface-raised px-2 py-1 text-base text-ink outline-none focus:ring-1 focus:ring-accent"
          value={held.mods.notes ?? ''}
          placeholder=""
          onChange={(event) => {
            const notes = event.target.value
            change({ mods: { ...held.mods, notes: notes.length === 0 ? undefined : notes } })
          }}
        />
      </PanelRow>
    </>
  )
}

const WEIGHT_TIPS =
  'Try 60 for kilos, 45lb, 20-30 for a range, or e1rm(80%, 50kg). Leave it empty for bodyweight.'

const REST_TIPS = 'Try 90 for seconds, or 3m for minutes. Leave it empty for no rest.'

export function RampFields({ line, onLine }: { line: AutoLine; onLine: (line: Line) => void }) {
  const keys = ['bar', 'jump', 'to'] as const
  const labels = { bar: 'Start at', jump: 'Climb by', to: 'Stop under' } as const
  return (
    <div className="space-y-2">
      {keys.map((key) => (
        <PanelRow key={key} label={labels[key]}>
          <NumberField
            boxed
            label={labels[key]}
            value={((held) => (held ? exprSource(held) : ''))(
              line.args.find(([name]) => name === key)?.[1],
            )}
            unit={key === 'to' ? UNITS.ratio : UNITS.kg}
            onValue={(value) => {
              if (value === null) {
                return
              }
              const args = [
                ...line.args.filter(([name]) => name !== key),
                [key, value] as [string, Expr],
              ]
              onLine({
                ...line,
                args: args.sort(
                  (a, b) => keys.indexOf(a[0] as 'bar') - keys.indexOf(b[0] as 'bar'),
                ),
              })
            }}
          />
        </PanelRow>
      ))}
      <RampRepsField line={line} onLine={onLine} />
      <p className="text-xs text-ink-muted">
        Sets climbing from the start, in those jumps, staying under that share of the first working
        weight.
      </p>
    </div>
  )
}

/**
 * The reps a ramp's sets take, written as the list they are — 10, 5, 3 — with the last one
 * repeating. Left empty, each set asks for fewer the nearer its weight gets to the working
 * one, which is right at whatever length the ramp turns out to be.
 */
function RampRepsField({ line, onLine }: { line: AutoLine; onLine: (line: Line) => void }) {
  const held = line.args.find(([name]) => name === 'reps')?.[1]
  const current =
    held?.type === 'array' ? held.items.map((item) => exprSource(item)).join(', ') : ''
  const [text, setText] = useState(current)
  const [bad, setBad] = useState(false)
  const [own, setOwn] = useState<string | null>(null)
  const select = useSelectOnFocus()
  const [seen, setSeen] = useState(current)
  if (seen !== current) {
    setSeen(current)
    if (own !== current) {
      setText(current)
      setBad(false)
    }
    setOwn(null)
  }

  const commit = (typed: string): boolean => {
    const trimmed = typed.trim()
    if (trimmed === '') {
      setOwn('')
      onLine({ ...line, args: line.args.filter(([name]) => name !== 'reps') })
      return true
    }
    try {
      const value = new Parser(`[${trimmed}]`).parseExpr()
      if (value.type !== 'array' || value.items.length === 0 || staticExprProblem(value) !== null) {
        return false
      }
      setOwn(value.items.map((item) => exprSource(item)).join(', '))
      onLine({
        ...line,
        args: [...line.args.filter(([name]) => name !== 'reps'), ['reps', value]],
      })
      return true
    } catch {
      return false
    }
  }

  return (
    <PanelRow label="Reps each set">
      <div>
        <input
          aria-label="Reps each set"
          aria-invalid={bad || undefined}
          className={`w-full rounded-lg bg-surface-raised px-2 py-1 text-base text-ink outline-none ${
            bad ? 'ring-1 ring-danger' : 'focus:ring-1 focus:ring-accent'
          }`}
          value={text}
          placeholder="Fewer as it nears the weight"
          onChange={(event) => {
            const changed = event.target.value
            setText(changed)
            setBad(!commit(changed) && changed.trim() !== '')
          }}
          onFocus={select.onFocus}
          onMouseUp={select.onMouseUp}
          onBlur={() => {
            if (!commit(text)) {
              setText(current)
            }
            setBad(false)
          }}
        />
        <p className="pt-0.5 text-xs text-ink-muted/80">
          Left empty, each set asks for fewer the nearer it gets to the working weight. Written out,
          the last number carries on for as many sets as the ramp takes.
        </p>
      </div>
    </PanelRow>
  )
}

/** A recognised progression, edited by its own fields. */
function PresetFields({
  line,
  identified,
  onLine,
}: {
  line: Line
  identified: { preset: Preset; params: Record<string, string> }
  onLine: (line: Line) => void
}) {
  return (
    <div className="space-y-2">
      {identified.preset.fields.map((field) => (
        <PresetParamField
          key={field.key}
          line={line}
          preset={identified.preset}
          params={identified.params}
          fieldKey={field.key}
          label={field.label}
          weightlike={field.kind === 'weight' || field.kind === 'step'}
          onLine={onLine}
        />
      ))}
      <p className="text-xs text-ink-muted">{identified.preset.summary(identified.params)}</p>
    </div>
  )
}

/**
 * One field of a preset's form. What it holds is a string the preset writes into its shape, so
 * a change only lands once it reads as a value and the whole block still builds — and a field
 * left empty or broken settles back to what the block holds.
 */
function PresetParamField({
  line,
  preset,
  params,
  fieldKey,
  label,
  weightlike,
  onLine,
}: {
  line: Line
  preset: Preset
  params: Record<string, string>
  fieldKey: string
  label: string
  weightlike: boolean
  onLine: (line: Line) => void
}) {
  const current = params[fieldKey] ?? ''
  const [text, setText] = useState(current)
  const [bad, setBad] = useState(false)
  const [own, setOwn] = useState<string | null>(null)
  const origin = useRef(current)
  const select = useSelectOnFocus()
  const [seen, setSeen] = useState(current)
  if (seen !== current) {
    setSeen(current)
    if (own !== current) {
      setText(current)
      setBad(false)
    }
    setOwn(null)
  }

  const commit = (typed: string): boolean => {
    const trimmed = typed.trim()
    if (trimmed === '') {
      return false
    }
    const written = weightlike && /^\d*\.?\d+$/.test(trimmed) ? `${trimmed}kg` : trimmed
    try {
      const value = new Parser(written).parseExpr()
      if (staticExprProblem(value) !== null) {
        return false
      }
      // The shape is what the preset builds; what the block asks for around its sets is the
      // block's own, and rebuilding the shape is no reason to lose it.
      const built = buildPreset(preset, { ...params, [fieldKey]: written })
      setOwn(written)
      onLine(line.type === 'dynamic' ? withBlockAround(built, blockAround(line)) : built)
      return true
    } catch {
      return false
    }
  }

  return (
    <PanelRow label={label}>
      <input
        aria-label={label}
        aria-invalid={bad || undefined}
        className={`w-full rounded-lg bg-surface-raised px-2 py-1 text-base text-ink outline-none ${
          bad ? 'ring-1 ring-danger' : 'focus:ring-1 focus:ring-accent'
        }`}
        value={text}
        onChange={(event) => {
          const changed = event.target.value
          setText(changed)
          setBad(!commit(changed) && changed.trim() !== '')
        }}
        onFocus={(event) => {
          origin.current = current
          select.onFocus(event)
        }}
        onMouseUp={select.onMouseUp}
        onBlur={() => {
          if (!commit(text)) {
            setText(current)
          }
          setBad(false)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
          } else if (event.key === 'Escape') {
            event.stopPropagation()
            commit(origin.current)
            setText(origin.current)
            setBad(false)
          }
        }}
      />
    </PanelRow>
  )
}

const CODE_HELP = [
  'This block runs every time the workout is planned, and decides the sets for you.',
  'yield 3 x 5 @ 60kg adds those sets. return stops the block there.',
  'It can look at sessions — everything you have done of this exercise since you started the template — as well as session, today so far, and history("Name") for everything you have ever logged.',
  'So let n = sessions.length, then yield 3 x 5 @ 60kg + 2.5kg * n, adds two and a half kilos every time.',
  'Give the block a name that says what it does; that name is what the card shows.',
]

/**
 * A small round icon riding on the code editor whose popover opens on hover or focus — the
 * help, the snippets. It floats clear of the code, and of the line editor it sits inside:
 * anchored where it was declared, so opening it is not what closes the editor around it, but
 * placed against the viewport, so that editor's own box never cuts it off.
 */
function CodeHint({
  label,
  icon,
  open,
  onOpen,
  children,
}: {
  label: string
  icon: ReactNode
  open: boolean
  onOpen: (open: boolean) => void
  children: ReactNode
}) {
  const held = useRef<HTMLSpanElement>(null)
  /** The icon is not outside its own popover, however the popover is placed. */
  const ours = (target: EventTarget | null): boolean =>
    held.current?.contains(target as Node) ?? false

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpen(next)
      }}
    >
      {/* Icon and panel share one hover, so travelling from one to the other keeps it open. */}
      <span
        ref={held}
        onPointerEnter={() => {
          onOpen(true)
        }}
        onPointerLeave={() => {
          onOpen(false)
        }}
      >
        {/* Hovering, focusing and pressing all open it; only leaving it closes it, so a press
            on the way to reading it is never what takes it away again. */}
        <PopoverAnchor asChild>
          <button
            type="button"
            aria-label={label}
            aria-expanded={open}
            className={`grid size-6 place-items-center rounded-full bg-surface-bright text-xs ring-1 ring-white/10 transition-colors hover:text-ink ${
              open ? 'text-ink' : 'text-ink-muted'
            }`}
            onFocus={() => {
              onOpen(true)
            }}
            onBlur={(event) => {
              // Focus travelling into the panel is focus arriving, not focus leaving.
              if (!ours(event.relatedTarget)) {
                onOpen(false)
              }
            }}
            onClick={() => {
              onOpen(true)
            }}
          >
            {icon}
          </button>
        </PopoverAnchor>
        <PopoverContent
          portal={false}
          side="top"
          align="end"
          sideOffset={6}
          collisionPadding={12}
          className="w-72 bg-surface-bright p-3 text-left"
          onOpenAutoFocus={(event) => {
            // The pointer is what opens this, and taking the focus off the code would be rude.
            event.preventDefault()
          }}
          keepsOpenOn={ours}
          onFocusOutside={(event) => {
            if (ours(event.target)) {
              event.preventDefault()
            }
          }}
        >
          {children}
        </PopoverContent>
      </span>
    </Popover>
  )
}

/**
 * A block, edited as the two things it is: what it does, and how. The name is a field and the
 * code is the code — the `dynamic ... { }` around them is never typed, so what is being edited
 * cannot stop being a block halfway through a keystroke. Both, with the help and the blocks
 * already written, read as one control.
 */
function CodeField({ line, onLine }: { line: DynamicLine; onLine: (line: Line) => void }) {
  const body = blockBody(line)
  const [text, setText] = useState(body)
  const [problem, setProblem] = useState<CodeProblem | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [snippetsOpen, setSnippetsOpen] = useState(false)
  const [own, setOwn] = useState<string | null>(null)
  const [seen, setSeen] = useState(body)
  if (seen !== body) {
    setSeen(body)
    if (own !== body) {
      setText(body)
      setProblem(null)
    }
    setOwn(null)
  }

  // Every custom dynamic block across the templates — this one included — offered as a
  // starting point. Blocks shaped like a preset are left out: they have their own fields.
  const state = useDataState()
  const board = useContext(BoardTemplate)
  const snippets = useMemo(() => {
    const seenSources = new Map<string, { label: string; line: Line }>()
    const nodes: TemplateNode[] = board === null ? [] : [board]
    for (const template of state.templates) {
      try {
        nodes.push(parseTemplate(template.script))
      } catch {
        // A template that does not parse has no snippets to offer.
      }
    }
    for (const node of nodes) {
      for (const workout of node.workouts) {
        for (const exercise of workoutExercises(workout)) {
          for (const held of exercise.lines) {
            if (held.type === 'dynamic' && identifyPreset(held) === null) {
              const source = lineSource(held)
              if (!seenSources.has(source)) {
                seenSources.set(source, { label: describeLine(held), line: held })
              }
            }
          }
        }
      }
    }
    return [...seenSources.entries()].map(([source, held]) => ({ source, ...held }))
  }, [state.templates, board])
  const offered = snippets.filter((snippet) => snippet.source !== lineSource(line))

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-2xl ring-1 ring-white/10">
        <div className="flex items-center gap-1 bg-surface-raised px-2 py-1.5">
          <input
            aria-label="What this block does"
            value={line.label}
            placeholder="What this does"
            className="min-w-0 flex-1 rounded-lg bg-transparent px-1.5 py-1 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted hover:bg-hover focus:bg-surface-bright"
            onChange={(event) => {
              onLine({ ...line, label: event.target.value })
            }}
          />
          <CodeHint
            label="Blocks from your templates"
            icon={<ListIcon aria-hidden className="size-3.5" />}
            open={snippetsOpen}
            onOpen={setSnippetsOpen}
          >
            <p className="pb-1 text-xs uppercase tracking-wide text-ink-muted">
              From your templates
            </p>
            {offered.length === 0 ? (
              <p className="text-xs text-ink-muted">
                Nothing yet — a block written in any template shows up here.
              </p>
            ) : (
              <div className="scroll-y -mx-1 max-h-56 overscroll-contain">
                {offered.map((snippet) => (
                  <button
                    key={snippet.source}
                    type="button"
                    title={snippet.source}
                    className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-xs text-ink-muted transition-colors hover:bg-hover hover:text-ink"
                    onClick={() => {
                      onLine(snippet.line)
                      setSnippetsOpen(false)
                    }}
                  >
                    {snippet.label}
                  </button>
                ))}
              </div>
            )}
          </CodeHint>
          <CodeHint label="How a code line works" icon="?" open={helpOpen} onOpen={setHelpOpen}>
            <div className="space-y-1.5">
              {CODE_HELP.map((help) => (
                <p key={help} className="text-xs text-ink-muted">
                  {help}
                </p>
              ))}
            </div>
          </CodeHint>
        </div>
        <CodeEditor
          label="Line"
          height="12rem"
          rounded="rounded-none"
          value={text}
          problem={problem}
          onChange={(changed) => {
            setText(changed)
            try {
              const parsed = blockFrom(changed)
              setOwn(blockBody(parsed))
              onLine({ ...parsed, label: line.label })
              setProblem(null)
            } catch (error) {
              // The wrapper the parse borrows opens four lines above the body's own.
              setProblem(
                error instanceof ScriptError
                  ? {
                      line: Math.max(1, error.line - 4),
                      column: error.column,
                      message: error.message,
                    }
                  : { line: 1, column: 1, message: String(error) },
              )
            }
          }}
        />
      </div>
      {problem === null ? null : <p className="text-xs text-danger">{problem.message}</p>}
    </div>
  )
}
